/**
 * HeaderUserMenu — account/support/logout dropdown used in the Dashboard header
 * (and optionally other authenticated pages). Replaces the old sidebar footer
 * controls the user asked to move up for visibility.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  UserCircle, LogOut, Headphones, ChevronDown, Shield,
  Users, ExternalLink, Wand2, Mail, Loader2, Lock, ShieldCheck,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HeaderUserMenu() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showPersonas, setShowPersonas] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!ref.current?.contains(e.target)) {
        setOpen(false);
        setShowPersonas(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  if (!user) return null;
  const initial = (user.name || user.email || "?").slice(0, 1).toUpperCase();
  const isMaster = (user.role || "").toLowerCase() === "super_admin"
                || (user.role || "").toLowerCase() === "admin"
                || user.email === "master@agentelead.pro";

  const togglePersonas = async () => {
    const next = !showPersonas;
    setShowPersonas(next);
    if (next && personas.length === 0) {
      setLoadingPersonas(true);
      try {
        const { data } = await axios.get(`${API}/master/demo-accounts`);
        setPersonas(data.accounts || []);
      } catch { /* silent */ }
      finally { setLoadingPersonas(false); }
    }
  };

  const impersonate = async (tenantId, label, sameTab = false) => {
    try {
      const { data } = await axios.post(`${API}/master/tenants/${tenantId}/impersonate`);
      if (!data?.access_token) { toast.error("No token returned"); return; }
      const url = `/impersonate?token=${encodeURIComponent(data.access_token)}&dest=/dashboard${sameTab ? "&backup=1" : ""}`;
      if (sameTab) { window.location.href = url; }
      else {
        window.open(url, "_blank", "noopener");
        toast.success(`Opened as ${label}`);
        setOpen(false); setShowPersonas(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const copyMagicLink = async (slug, label) => {
    try {
      const { data } = await axios.post(`${API}/auth/magic-link?slug=${slug}&ttl_days=14`);
      await navigator.clipboard.writeText(data.url);
      toast.success(`Magic link · ${label} copied (14d)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="header-user-menu-trigger"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 pl-1.5 pr-2.5 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] hover:border-[#3B82F6] transition-colors"
      >
        <div className="w-6 h-6 rounded-full bg-[#C9A24E] text-[#0A1233] flex items-center justify-center text-[11px] font-bold">{initial}</div>
        <span className="hidden md:inline text-[12px] font-semibold max-w-[140px] truncate">{user.name || user.email.split("@")[0]}</span>
        {isMaster && (
          <span className="hidden lg:inline text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#8B5CF6]/15 text-[#8B5CF6]">Master</span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-[var(--al-text-muted)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="header-user-menu-panel"
          className="absolute right-0 mt-2 w-[290px] rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] shadow-xl py-1 z-40"
        >
          <div className="px-3 py-2 border-b border-[var(--al-border)]">
            <div className="text-[12.5px] font-bold leading-tight">{user.name || user.email.split("@")[0]}</div>
            <div className="text-[10.5px] text-[var(--al-text-muted)] truncate">{user.email}</div>
          </div>
          <button
            type="button"
            data-testid="header-user-account"
            onClick={() => { setOpen(false); navigate("/account"); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-[var(--al-text)] hover:bg-[var(--al-bg)] transition-colors"
          >
            <UserCircle className="w-3.5 h-3.5" /> {t("sidebar.account") || "Account info"}
          </button>
          <button
            type="button"
            data-testid="header-user-support"
            onClick={() => { setOpen(false); window.open("https://wa.me/?text=Support%20request", "_blank", "noopener,noreferrer"); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-[var(--al-text)] hover:bg-[var(--al-bg)] transition-colors"
          >
            <Headphones className="w-3.5 h-3.5" /> {t("sidebar.support") || "Support"}
          </button>
          {isMaster && (
            <>
              <button
                type="button"
                data-testid="header-user-master-console"
                onClick={() => { setOpen(false); navigate("/master"); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-[#7C3AED] font-semibold hover:bg-[var(--al-bg)] transition-colors"
              >
                <Shield className="w-3.5 h-3.5" /> Master Console
              </button>
              <button
                type="button"
                data-testid="header-user-switch-persona"
                onClick={togglePersonas}
                className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-[#C9A24E] font-semibold hover:bg-[var(--al-bg)] transition-colors"
              >
                <Users className="w-3.5 h-3.5" /> Switch persona
                <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${showPersonas ? "rotate-180" : ""}`} />
              </button>
              {showPersonas && (
                <div data-testid="header-user-personas-list"
                     className="border-t border-[var(--al-border)] bg-[var(--al-bg)] max-h-[320px] overflow-y-auto">
                  {loadingPersonas ? (
                    <div className="flex items-center justify-center py-3 text-[11px] text-[var(--al-text-muted)] gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                    </div>
                  ) : personas.length === 0 ? (
                    <div className="px-3 py-3 text-[11px] text-[var(--al-text-muted)]">
                      No demo accounts yet — open <a href="/master/demo-accounts" className="text-[#C9A24E] font-bold">Demo Profiles</a>.
                    </div>
                  ) : personas.map((a) => {
                    const isTrial = a.package === "personalizado";
                    return (
                      <div key={a.tenant_id}
                           data-testid={`header-persona-${a.slug}`}
                           className="px-3 py-2 hover:bg-[var(--al-card-bg)] border-b border-[var(--al-border)] last:border-b-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${isTrial ? "bg-rose-500/20 text-rose-600" : "bg-emerald-500/20 text-emerald-600"}`}>
                            {isTrial ? <Lock className="w-2.5 h-2.5" /> : <ShieldCheck className="w-2.5 h-2.5" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-bold truncate">{a.label}</div>
                          </div>
                          <span className={`text-[8px] font-bold px-1 py-0.5 rounded uppercase tracking-wider ${isTrial ? "bg-rose-500/15 text-rose-600" : "bg-emerald-500/15 text-emerald-700"}`}>
                            {a.package}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => impersonate(a.tenant_id, a.label)}
                                  data-testid={`header-persona-open-${a.slug}`}
                                  className="flex-1 h-6 px-1 rounded bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[10px] font-bold inline-flex items-center justify-center gap-1">
                            <ExternalLink className="w-2.5 h-2.5" /> Open
                          </button>
                          <button onClick={() => copyMagicLink(a.slug, a.label)}
                                  data-testid={`header-persona-magic-${a.slug}`}
                                  className="flex-1 h-6 px-1 rounded bg-[#F59E0B] hover:bg-[#D97706] text-[#1A0E36] text-[10px] font-bold inline-flex items-center justify-center gap-1">
                            <Wand2 className="w-2.5 h-2.5" /> Magic
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <a href="/master/demo-accounts"
                     className="block px-3 py-2 text-[11px] text-[#C9A24E] font-bold hover:bg-[var(--al-card-bg)] text-center">
                    Manage demo profiles →
                  </a>
                </div>
              )}
            </>
          )}
          <div className="border-t border-[var(--al-border)] my-1" />
          <button
            type="button"
            data-testid="header-user-logout"
            onClick={() => { setOpen(false); logout(); navigate("/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-rose-600 hover:bg-rose-50 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> {t("sidebar.logout")}
          </button>
        </div>
      )}
    </div>
  );
}
